import type { Row } from '../../../core/view-manager/row.js';
import type {
  BarState,
  PositionTransform,
  PositionTransformCtx,
} from '../extensions/types.js';
import type { GanttSingleView } from '../gantt-view-manager.js';
import { MS_PER_DAY } from './time-axis-math.js';

/**
 * Threads a bar state through every registered
 * {@link PositionTransform} in priority order. The first transform sees
 * the raw cell values; each subsequent transform sees the previous
 * result. The return value is the final committed state.
 */
export function runPositionPipeline(
  initial: BarState,
  transforms: readonly PositionTransform[],
  ctx: PositionTransformCtx
): BarState {
  let state = initial;
  for (const transform of transforms) {
    state = transform.transform(state, ctx);
  }
  return state;
}

/**
 * Reads the configured start/end cells for a row and constructs the raw
 * `BarState`. A row with a start and no end is a bar of one day from its
 * start. Without a start the row is not drawn ("Delete bar" clears both
 * cells).
 */
export function readBarStateFromCells(
  view: GanttSingleView,
  row: Row
): BarState | null {
  const startCol = view.startDateCol$.value;
  const endCol = view.endDateCol$.value;
  if (!startCol || !endCol) return null;

  const startCell = view.cellGetOrCreate(row.rowId, startCol);
  const endCell = view.cellGetOrCreate(row.rowId, endCol);

  const startRaw = startCell.jsonValue$.value;
  const endRaw = endCell.jsonValue$.value;

  if (typeof startRaw !== 'number') return null;

  return {
    rowId: row.rowId,
    startMs: startRaw,
    endMs: typeof endRaw === 'number' ? endRaw : startRaw + MS_PER_DAY,
  };
}

/**
 * Clears start/end date cells for the given rows, unscheduling them
 * without deleting the backing data-view row. Used by the bar context
 * menu's "Delete bar" action and by Backspace on a bar selection —
 * both mean "take the bar off the timeline", not "drop the record".
 */
export function clearBarStateCells(
  view: GanttSingleView,
  rowIds: readonly string[]
): void {
  const startCol = view.startDateCol$.value;
  const endCol = view.endDateCol$.value;
  if (!startCol || !endCol) return;
  const startProp = view.propertyGetOrCreate(startCol);
  const endProp = view.propertyGetOrCreate(endCol);
  for (const rowId of rowIds) {
    startProp.valueSet(rowId, undefined);
    endProp.valueSet(rowId, undefined);
  }
}
